import { activeSiteConfig } from "@config/site/site-config";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useAuth } from "@lib/auth/useAuth";
import type { PlpStoreLookup } from "@lib/utils/plp/normalizeStoresForPlp";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

type UsePlpStoresParams<TData = PlpStoreLookup[]> = {
    locale: string;
    queryConfig?: Omit<UseQueryOptions<PlpStoreLookup[], Error, TData>, "queryKey" | "queryFn">;
};

/** Fetches all market stores for PLP store filter label lookup. */
export const usePlpStores = <TData = PlpStoreLookup[]>({
    locale,
    queryConfig = {},
}: UsePlpStoresParams<TData>) => {
    const { session, status } = useAuth();
    const country = activeSiteConfig.country;
    const queryKey = [QueryKeys.getStores, "plp", country?.toUpperCase(), locale?.toUpperCase()];
    const { enabled: enabledByQueryConfig = true, ...restQueryConfig } = queryConfig;
    const isAuthenticated = !!session?.accessToken && status === "authenticated";
    const enabled =
        typeof enabledByQueryConfig === "function"
            ? (query) => enabledByQueryConfig(query) && isAuthenticated
            : enabledByQueryConfig && isAuthenticated;

    return useQuery<PlpStoreLookup[], Error, TData>({
        queryKey,
        queryFn: async () => {
            const searchParams = new URLSearchParams({ locale });
            const response = await fetch(`/api/stores?${searchParams}`, { cache: "no-store" });

            if (!response.ok) {
                throw new Error(`Failed to fetch PLP stores: ${response.status}`);
            }

            return response.json() as Promise<PlpStoreLookup[]>;
        },
        ...restQueryConfig,
        enabled,
    });
};
